import { useState, useEffect } from "react";
import {
    Sheet,
    SheetContent,
    SheetHeader,
    SheetTitle,
    SheetDescription,
    SheetFooter,
} from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2 } from "lucide-react";
import { Textarea } from "@/components/ui/textarea";

const QuickInfoEditSheet = ({
    open,
    onOpenChange,
    label,
    currentValue,
    currentSubValue,
    onSave,
    // Optional: if dealing with a totally custom item
    isCustom = false,
}) => {
    const [isLoading, setIsLoading] = useState(false);
    const [editLabel, setEditLabel] = useState(label || "");
    const [value, setValue] = useState(currentValue || "");
    const [subValue, setSubValue] = useState(currentSubValue || "");

    useEffect(() => {
        if (open) {
            const timer = setTimeout(() => {
                setEditLabel(label || "");
                setValue(currentValue || "");
                setSubValue(currentSubValue || "");
            }, 0);
            return () => clearTimeout(timer);
        }
    }, [open, label, currentValue, currentSubValue]);

    const handleSave = async () => {
        setIsLoading(true);
        // If custom, we might want to generate infoType from label if not provided
        // But the parent usually handles upsert keys.
        // For simplicity, pass back the potentially edited label.

        await onSave(value, subValue, isCustom ? editLabel : undefined);
        setIsLoading(false);
        onOpenChange(false);
    };

    return (
        <Sheet open={open} onOpenChange={onOpenChange}>
            <SheetContent side="bottom" onOpenAutoFocus={(e) => e.preventDefault()} className="rounded-t-[2rem]">
                <SheetHeader className="mb-6 text-left">
                    <SheetTitle>
                        {isCustom && !label ? "Add House Info" : `Edit ${label || "Info"}`}
                    </SheetTitle>
                    <SheetDescription>
                        {isCustom
                            ? "Add useful details about your property."
                            : "Update the location or details for this item."}
                    </SheetDescription>
                </SheetHeader>

                <div className="space-y-4 mb-6">
                    {isCustom && (
                        <div className="space-y-2">
                            <Label htmlFor="label">Title</Label>
                            <Input
                                id="label"
                                value={editLabel}
                                onChange={(e) => setEditLabel(e.target.value)}
                                placeholder="e.g. WiFi Password, Alarm Code"
                            />
                        </div>
                    )}

                    <div className="space-y-2">
                        <Label htmlFor="value">
                            {isCustom ? "Value / Location" : "Location / Value"}
                        </Label>
                        <Input
                            id="value"
                            value={value}
                            onChange={(e) => setValue(e.target.value)}
                            placeholder={isCustom ? "e.g. 123456" : "e.g. In the garage"}
                        />
                    </div>

                    <div className="space-y-2">
                        <Label htmlFor="subValue">Additional Details (Optional)</Label>
                        <Textarea
                            id="subValue"
                            value={subValue}
                            onChange={(e) => setSubValue(e.target.value)}
                            placeholder="Any extra context..."
                            className="resize-none"
                            rows={3}
                        />
                    </div>
                </div>

                <SheetFooter>
                    <Button
                        onClick={handleSave}
                        disabled={isLoading || !value.trim() || (isCustom && !editLabel.trim())}
                        className="w-full"
                    >
                        {isLoading ? (
                            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        ) : (
                            "Save Changes"
                        )}
                    </Button>
                </SheetFooter>
            </SheetContent>
        </Sheet>
    );
};

export default QuickInfoEditSheet;
